import { Link } from "@tanstack/react-router";
import { useQuery } from "@tanstack/react-query";
import { useServerFn } from "@tanstack/react-start";
import { getUsage } from "@/lib/usage.functions";
import { useAuth } from "@/hooks/use-auth";
import { Check } from "lucide-react";

export function UsageBadge({ compact = false }: { compact?: boolean }) {
  const { session } = useAuth();
  const fetchUsage = useServerFn(getUsage);
  const headers = session?.access_token
    ? { Authorization: `Bearer ${session.access_token}` }
    : undefined;

  const q = useQuery({
    queryKey: ["usage"],
    queryFn: () => fetchUsage({ headers }),
    enabled: !!headers,
  });

  if (!session || q.isLoading || !q.data) return null;

  if (q.data.plan === "pro") {
    return (
      <div
        data-testid="usage-badge"
        className="inline-flex shrink-0 items-center gap-1.5 rounded-full border border-accent/30 bg-accent/10 px-3 py-1 text-xs font-medium text-accent"
      >
        <Check className="h-3.5 w-3.5 shrink-0" />
        {compact ? "Pro" : "Pro — Unlimited generations"}
      </div>
    );
  }

  const used = q.data.used;
  const limit = q.data.limit ?? 3;
  const pct = Math.min(100, (used / limit) * 100);

  if (compact) {
    return (
      <div
        data-testid="usage-badge"
        className="inline-flex shrink-0 items-center gap-2 rounded-full border border-border bg-muted/40 px-3 py-1 text-xs"
      >
        <span className="whitespace-nowrap font-medium text-foreground">
          {used}/{limit} used
        </span>
        <span className="h-1.5 w-10 shrink-0 overflow-hidden rounded-full bg-secondary" aria-hidden>
          <span
            className="block h-full rounded-full bg-primary transition-all"
            style={{ width: `${pct}%` }}
          />
        </span>
        {q.data.daysUntilReset != null && (
          <span className="whitespace-nowrap text-accent">Resets in {q.data.daysUntilReset}d</span>
        )}
        <Link
          to="/pricing"
          className="whitespace-nowrap font-semibold text-primary hover:underline"
        >
          Upgrade →
        </Link>
      </div>
    );
  }

  return (
    <div
      data-testid="usage-badge"
      className="rounded-lg border border-border bg-card/80 px-3 py-2 text-xs"
    >
      <div className="font-medium text-foreground">
        {used}/{limit} free generations used
      </div>
      <div className="mt-1.5 h-1.5 overflow-hidden rounded-full bg-secondary">
        <div
          className="h-full rounded-full bg-primary transition-all"
          style={{ width: `${pct}%` }}
        />
      </div>
      {q.data.daysUntilReset != null && (
        <p className="mt-1.5 text-accent">
          Resets in {q.data.daysUntilReset} day{q.data.daysUntilReset === 1 ? "" : "s"}
        </p>
      )}
      <Link to="/pricing" className="mt-1 inline-block font-semibold text-primary hover:underline">
        Upgrade →
      </Link>
    </div>
  );
}
